
import React, { useCallback, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, Plus, Globe, X, Copy, Check, Sparkles, Zap } from "lucide-react";
import VoiceInputButton from "@/components/voice-input/VoiceInputButton";
import FileUpload from "./FileUpload";
import { ChatInputProps, USER_ACCESSIBLE_SPECIALISTS, DEFAULT_GEMINI_FLASH_MODEL } from '@flowstate/types';
import type { ReasoningEffort } from '@flowstate/types';
import { logger } from "@/lib/logger";
import { ModelPicker } from "./ModelPicker";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { NinjaStarFlyout } from "@/components/ui/ninja-star-flyout";

const ChatInput = ({
  input,
  setInput,
  handleSendMessage,
  isLoading,
  conversationId,
  onFileUploaded,
  pendingAttachments = [],
  onWebSearchClick,
  onWorkflowClick,
  selectedSpecialist,
  onSpecialistChange,
  selectedProvider = 'gemini',
  onProviderChange,
  selectedModel = DEFAULT_GEMINI_FLASH_MODEL,
  onModelChange,
  reasoningEffort,
  onReasoningEffortChange,
  isGeminiDelegated = false,
}: ChatInputProps & {
  reasoningEffort?: ReasoningEffort;
  onReasoningEffortChange?: (effort: ReasoningEffort) => void;
}) => {
  // Web search mode state
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  // Copy state
  const [copied, setCopied] = useState(false);
  // Specialist popover state
  const [specialistOpen, setSpecialistOpen] = useState(false);
  const showModelPicker = Boolean(onProviderChange && onModelChange);
  const showWorkflow = Boolean(onWorkflowClick);
  const composerActionCount =
    3 + (showModelPicker ? 1 : 0) + (showWorkflow ? 1 : 0);
  let slotIdx = 0;
  const slot = {
    file: ++slotIdx,
    model: showModelPicker ? ++slotIdx : null,
    specialist: ++slotIdx,
    workflow: showWorkflow ? ++slotIdx : null,
    web: ++slotIdx,
  };
  // Use a temporary ID for uploads before conversation is created
  const tempConversationId = React.useRef(`temp-${Date.now()}`);

  // Handle form submission
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    logger.info("Form submitted with input:", { input });
    if (input.trim()) {
      handleSendMessage();
    }
  }, [input, handleSendMessage]);

  // Handle voice transcription
  const handleVoiceTranscription = useCallback((text: string) => {
    logger.info("Voice transcription received:", { text });
    // If we already have some text in the input, append the transcription
    if (input.trim().length > 0) {
      setInput(prev => `${prev} ${text}`);
    } else {
      setInput(text);
    }
  }, [input, setInput]);

  // Handle web search toggle
  const handleWebSearchToggle = useCallback(() => {
    setWebSearchEnabled(prev => !prev);
    if (onWebSearchClick) {
      onWebSearchClick();
    }
  }, [onWebSearchClick]);

  // Handle copy input text
  const handleCopyInput = useCallback(async () => {
    if (input.trim()) {
      await navigator.clipboard.writeText(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [input]);

  return (
    <div className="space-y-2">
      {/* Pending attachments display */}
      {pendingAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-secondary/30 rounded-lg">
          {pendingAttachments.map((attachment) => (
            <div key={attachment.id} className="flex items-center gap-2 bg-background px-2 py-1 rounded-md text-xs">
              <span className="truncate max-w-[150px]">{attachment.name}</span>
            </div>
          ))}
        </div>
      )}
      {/* Main chat input - Modern design */}
      <form
        onSubmit={handleSubmit}
        className="flex gap-2 items-end"
      >
        <div className="flex-1 relative rounded-2xl border border-input bg-background shadow-sm hover:shadow-md transition-shadow duration-200 focus-within:shadow-lg focus-within:border-primary/50">
          <div className="absolute left-2 bottom-2 flex items-center z-10">
            <VoiceInputButton 
              onTranscription={handleVoiceTranscription}
              isDisabled={isLoading}
            />
          </div>
          
          {/* Web Search Mode Badge */}
          {webSearchEnabled && (
            <div className="absolute top-2 left-2 z-10">
              <Badge 
                variant="secondary" 
                className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 text-xs flex items-center gap-1 pl-1.5 pr-2 py-0.5"
              >
                <Globe className="h-3 w-3" />
                <span>Web Search</span>
                <Button
                  onClick={() => setWebSearchEnabled(false)}
                  className="ml-0.5 hover:bg-blue-500/20 rounded-full p-0.5 transition-colors"
                  type="button"
                >
                  <X className="h-2.5 w-2.5" />
                </Button>
              </Badge>
            </div>
          )}
          
          {/* Specialist Selection Badge — only shown when manually pinned */}
          {selectedSpecialist && (
            <div className={`absolute top-2 left-2 z-10 ${webSearchEnabled ? 'left-[calc(theme(spacing.2)+7rem)]' : ''}`}>
              <Badge 
                variant="secondary" 
                className="bg-primary/10 text-primary dark:text-muted-foreground border-primary/20 text-xs flex items-center gap-1 pl-1.5 pr-2 py-0.5"
              >
                <span className="text-xs">{USER_ACCESSIBLE_SPECIALISTS.find(s => s.id === selectedSpecialist)?.icon}</span>
                <span>{USER_ACCESSIBLE_SPECIALISTS.find(s => s.id === selectedSpecialist)?.name}</span>
                <Button
                  onClick={() => onSpecialistChange?.(undefined)}
                  className="ml-0.5 hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                  type="button"
                  aria-label="Switch back to auto routing"
                >
                  <X className="h-2.5 w-2.5" />
                </Button>
              </Badge>
            </div>
          )}
          
          <Textarea
            placeholder={webSearchEnabled ? "Ask a question to search the web..." : "Message Cortex..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (input.trim()) {
                  handleSendMessage();
                }
              }
            }}
            rows={3}
            className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 pl-4 pr-12 pt-10 pb-14 text-base rounded-2xl resize-none max-h-32 overflow-y-auto"
            disabled={isLoading}
            aria-label="Chat input"
          />
          {/* Quick actions — top-right of composer */}
          <div className="absolute right-2 top-2 z-10">
            <NinjaStarFlyout
              variant="composer"
              itemCount={composerActionCount}
              data-testid="cortex-composer-quick-actions"
              aria-label="Composer quick actions"
              trigger={
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="lightning-trigger chat-header-icon-btn h-8 w-8 min-h-8 min-w-8 shrink-0 rounded-full border border-border/50 bg-background/90 text-primary shadow-sm hover:bg-accent"
                        disabled={isLoading}
                        aria-label="Open quick actions"
                      >
                        <Zap className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Quick actions</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              }
            >
              <div className="ninja-item" style={{ "--i": slot.web } as React.CSSProperties}>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant={webSearchEnabled ? "default" : "ghost"}
                        size="icon"
                        className={`h-8 w-8 rounded-full transition-all ${
                          webSearchEnabled
                            ? "bg-blue-500 hover:bg-blue-600 text-white"
                            : "hover:bg-accent"
                        }`}
                        onClick={handleWebSearchToggle}
                        disabled={isLoading}
                      >
                        <Globe className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{webSearchEnabled ? "Web search: ON" : "Enable web search"}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {showWorkflow && (
                <div className="ninja-item" style={{ "--i": slot.workflow } as React.CSSProperties}>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-full hover:bg-accent"
                          onClick={onWorkflowClick}
                          disabled={isLoading}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Run workflow</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}

              <div className="ninja-item" style={{ "--i": slot.specialist } as React.CSSProperties}>
                <TooltipProvider>
                  <Tooltip>
                    <Popover open={specialistOpen} onOpenChange={setSpecialistOpen}>
                      <PopoverTrigger asChild>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant={selectedSpecialist ? "default" : "ghost"}
                            size="icon"
                            className={`h-8 w-8 rounded-full transition-all ${
                              selectedSpecialist
                                ? "bg-primary hover:bg-primary text-white"
                                : "hover:bg-accent"
                            }`}
                            disabled={isLoading}
                          >
                            {selectedSpecialist
                              ? <span className="text-xs">{USER_ACCESSIBLE_SPECIALISTS.find(s => s.id === selectedSpecialist)?.icon}</span>
                              : <Sparkles className="h-4 w-4" />
                            }
                          </Button>
                        </TooltipTrigger>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 p-2" align="end">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground px-2 mb-2">
                            Specialist Routing
                          </p>
                          <Button
                            variant="ghost"
                            type="button"
                            onClick={() => {
                              onSpecialistChange?.(undefined);
                              setSpecialistOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 h-auto rounded-md text-left text-sm transition-colors justify-start ${
                              !selectedSpecialist
                                ? "bg-primary/10 text-primary dark:text-primary-foreground"
                                : "hover:bg-muted"
                            }`}
                          >
                            <Sparkles className="h-4 w-4 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">Auto</p>
                              <p className="text-xs text-muted-foreground truncate">Cortex picks the best specialist</p>
                            </div>
                          </Button>
                          <div className="border-t border-border/50 my-1.5" />
                          {USER_ACCESSIBLE_SPECIALISTS.map((specialist) => (
                            <Button
                              key={specialist.id}
                              variant="ghost"
                              type="button"
                              onClick={() => {
                                onSpecialistChange?.(specialist.id);
                                setSpecialistOpen(false);
                              }}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 h-auto rounded-md text-left text-sm transition-colors justify-start ${
                                selectedSpecialist === specialist.id
                                  ? "bg-primary/10 text-primary dark:text-primary-foreground"
                                  : "hover:bg-muted"
                              }`}
                            >
                              <span>{specialist.icon}</span>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{specialist.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{specialist.description}</p>
                              </div>
                            </Button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <TooltipContent>
                      <p>{selectedSpecialist
                        ? `Specialist: ${USER_ACCESSIBLE_SPECIALISTS.find(s => s.id === selectedSpecialist)?.name}`
                        : "Auto specialist routing"
                      }</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {showModelPicker ? (
                <div className="ninja-item ninja-flyout-control" style={{ "--i": slot.model } as React.CSSProperties}>
                  <ModelPicker
                    selectedProvider={selectedProvider}
                    onProviderChange={onProviderChange!}
                    selectedModel={selectedModel}
                    onModelChange={onModelChange!}
                    reasoningEffort={reasoningEffort}
                    onReasoningEffortChange={onReasoningEffortChange}
                    isGeminiDelegated={isGeminiDelegated}
                    disabled={isLoading}
                  />
                </div>
              ) : null}

              <div className="ninja-item ninja-flyout-control" style={{ "--i": slot.file } as React.CSSProperties}>
                <FileUpload
                  conversationId={conversationId || tempConversationId.current}
                  onFileUploaded={onFileUploaded}
                  disabled={isLoading}
                  buttonClassName="h-8 w-8 rounded-full hover:bg-accent"
                />
              </div>
            </NinjaStarFlyout>
          </div>

          {/* Send + copy — bottom-right */}
          <div className="absolute right-2 bottom-2 flex items-end gap-1 z-10">
            <div className="flex items-center gap-1">
            {/* Copy button (shows when text is present) */}
            {input.trim() && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={handleCopyInput}
                      className="h-8 w-8 rounded-lg hover:bg-accent"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{copied ? 'Copied!' : 'Copy text'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Send button */}
            <Button 
              type="submit" 
              size="icon"
              disabled={!input.trim() || isLoading}
              className={`h-8 w-8 rounded-lg ${
                input.trim() && !isLoading
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
            >
              <Send className="h-4 w-4" />
            </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
